/* eslint-disable prefer-destructuring */
import SyncEncounters from '@/mixins/SyncEncounters'
import WoundFooter from '@/mixins/final-note/WoundFooter'
import themeConfig from '@themeConfig'
import { v4 as uuidv4 } from 'uuid'
import { mapGetters } from 'vuex'
import dayjs from 'dayjs'
import Vue from 'vue'
import {
  addTreatmentToReservedData,
  removeTreatmentFromReservedData,
} from '@/utils/handleAmnioRequiredData'
import generateAddendum from '@/mixins/final-note/addendums/Addendums'

export default {
  mixins: [SyncEncounters, WoundFooter, generateAddendum],
  data() {
    return {
      noteKey: 0,
      isRedacted: false,
      output: null,
      addendum: null,
      addingAddendum: false,
      appName: themeConfig.app.name,
      showPaymentModal: false,
      syncSuccessful: false,
      paymentCalculated: false,
      deferredCareDebounceTimer: null,
    }
  },
  computed: {
    ...mapGetters(['isOnline']),
  },
  watch: {
    syncSuccessful() {
      // If the sync was initiated to calculate the payment total, show the estimated payment modal
      if (this.syncSuccessful && this.showPaymentModal) {
        this.showPaymentModal = false
        if (!this.encounter.is_signed) this.encounter.is_synced = false
        this.$store.commit('encounters/updateEncounter', { encounter: this.encounter })
        this.paymentModal()
        this.paymentCalculated = true
      }
    },
  },
  mounted() {
    // Make sure we're on the correct practice type route
    const targetRoute = {
      name: this.$store.getters['baseData/practiceTypeFromId'](this.encounter.practice_type_id).noteRoute,
      query: { id: this.encounter.id },
    }

    // Only push if the target is different from the current route
    if (
      this.practiceType !== this.encounter.practice_type_id &&
      (this.$route.name !== targetRoute.name || this.$route.query.id !== targetRoute.query.id)
    ) {
      this.$router.push(targetRoute)
    }

    // If deferred care, set validate to true
    if (this.encounter.is_deferred_care) {
      this.encounter.is_validated = true
    }
  },
  methods: {
    formatDate(date) {
      return this.$date(date).format('MM/DD/YYYY')
    },
    formatDateTime(dateTime) {
      return this.$date(dateTime).format('M/D/YYYY h:mm:ss A')
    },
    calculatePayment() {
      let disabledMessage = false
      if (this.addingAddendum) {
        disabledMessage = 'Please finish adding the addendum to enable estimated payment calculation.'
      }
      if (this.encounter.is_deferred_care) {
        disabledMessage = 'Estimated payment calculation is not available for deferred care encounters.'
      }
      if (this.encounter.is_signed && !this.encounter.is_synced) {
        disabledMessage = 'Either "Unsign Note" or "Sync Note" to enable calculate estimated payment.'
      }
      if (!this.paymentCalculated && disabledMessage) {
        this.$root.confirm({
          titleIconColor: 'warning',
          title: 'Estimated Payment Calculation Disabled',
          body: disabledMessage,
          cancel: false,
        })
      } else if (this.paymentCalculated || (this.encounter.is_signed && this.encounter.is_synced)) {
        this.paymentModal()
      } else {
        this.showPaymentModal = true
        this.syncNote()
      }
    },
    paymentModal() {
      this.$root.confirm({
        titleIcon: this.icons.mdiCurrencyUsd,
        titleIconColor: 'success',
        title: 'Estimated Payment',
        body: `Estimated payment for this encounter: ${this.$custom.formatCurrency(
          Number(this.encounter.estimated_payment || 0) + Number(this.encounter.pay_total_amnio || 0)
        )}`,
        cancel: false,
      })
    },
    printNote() {
      window.print()
    },
    closeEncounterNote() {
      const practiceType = this.$store.getters['baseData/practiceTypeFromId'](this.encounter.practice_type_id)
      if ((this.encounter.is_signed && this.encounter.is_synced) || this.encounter.is_deferred_care) {
        this.exit()
      } else {
        this.$store.dispatch('loading', true)
        this.$router.push({
          name: practiceType.encounterRoute,
          query: { id: this.encounter.id },
        })
      }
    },
    deferredCareUpdated() {
      // Debounce
      clearTimeout(this.deferredCareDebounceTimer)
      this.deferredCareDebounceTimer = setTimeout(() => {
        this.$store.commit('encounters/updateEncounter', { encounter: this.encounter })
      }, 500)
    },
    deleteDeferredEncounterNote() {
      this.$root.confirm({
        title: 'Delete Deferred Care Encounter Note?',
        subTitle: 'Warning: This operation cannot be undone!',
        body: 'Are you sure you wish to delete this encounter note?',
        confirm: 'Delete Note',
        confirmIcon: this.icons.mdiTrashCan,
        confirmColor: 'error',
      }).then(result => {
        if (result) {
          if (this.encounter.can_be_deleted) {
            this.$store.commit('encounters/deleteEncounter', this.encounter)
          } else {
            this.encounter.deleted = true
            this.encounter.is_synced = false
            this.$store.commit('encounters/updateEncounter', { encounter: this.encounter, patient: this.patient })
          }
          this.$store.dispatch('notify', { value: 'Encounter note deleted successfully.' })
          this.exit()
        }
      })
    },
    signNote() {
      if ( !this.encounter.final_note?.trim() && !this.encounter.is_deferred_care ) {
        // Notify the user in case the final note was not generated and prevent them from signing the note.
        this.$root.confirm({
          title: 'Final note is empty!',
          titleIconColor: 'error',
          body: 'The final note was not generated and therefore cannot be signed. Please close and reopen the note preview to regenerate it.',
          confirm: 'Close Preview',
          cancel: false,
        }).then(result => {
          if (result) {
            this.closeEncounterNote()
          }
        })
      } else {
        const hasFacesheet = this.patient.all_attachments
              ? this.patient.all_attachments.findIndex(x => x.attachment_type === 'facesheets') !== -1
              : false

        const skipFacesheetCheck = hasFacesheet || this.encounter.is_deferred_care

        this.$root.confirm({
          title: skipFacesheetCheck ? 'Sign Encounter Note?' : 'Facesheet missing!',
          titleIconColor: skipFacesheetCheck ? false : 'error',
          body: skipFacesheetCheck
            ? 'Are you sure you wish to sign this encounter note?'
            : 'Are you sure you wish to sign this encounter note even with a missing facesheet?',
          confirm: 'Sign Note',
          confirmIcon: this.icons.mdiFileSign,
          confirmColor: 'warning',
        }).then(async result => {
          if (result) {
            if (this.encounter.is_deferred_care) {
              this.generateDeferredCareFinalNote()
            }
            this.encounter.is_quick_entry_validated = !this.encounter.is_deferred_care
            this.encounter.is_signed = true
            this.encounter.is_synced = false
            this.encounter.signed_by_user_id = this.$authUser.userId()
            this.encounter.signed_time = this.$custom.utcNow()
            this.encounter.modified = this.$custom.utcNow()
            // Set modified by scribe to null since only the actual provider can sign the note.
            this.encounter.modified_by_scribe_id = null
            this.$store.commit('encounters/updateEncounter', { encounter: this.encounter })

            // ~ ~ ~ Update fields at patient level if applicable.
            let updatedPatient = false
            // Get the last signed note's visit date
            const lastSignedNote = Vue.store.getters['encounters/lastSignedEncounter'](this.patient.id)
            const lastSignedNoteDos = lastSignedNote.visit_date ? dayjs(lastSignedNote.visit_date)
              .startOf('day') : false
            const encounterVisitDate = dayjs(this.encounter.visit_date)
              .startOf('day')

            // Check if encounter visit date is the latest or within the last 30 days
            const today = dayjs()
              .startOf('day')
            const thirtyDaysAgo = today.subtract(30, 'days')
            const isLatestDos = lastSignedNoteDos ? encounterVisitDate.isSameOrAfter(lastSignedNoteDos, 'day') : true
            const isWithin30Days = encounterVisitDate.isSameOrAfter(thirtyDaysAgo, 'day') && encounterVisitDate.isBefore(today.add(1, 'day'), 'day')

            // Check if patient's facility needs to be updated
            if (!this.patient.place_of_service_id || isWithin30Days || isLatestDos) {
              this.patient.place_of_service_id = this.encounter.place_of_service_id
              updatedPatient = true
            }
            // Check if this is the patient's latest visit.
            if (isLatestDos) {
              this.patient.latest_visit_date = this.encounter.visit_date
              updatedPatient = true
            }

            // If updated, commit the change to the store
            if (updatedPatient) {
              this.$store.commit('patients/updatePatient', this.patient)
            }
            // ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~

            // ~ ~ ~ Add treatments from the current encounter to amnioReqData (only wounds with practice_type_id === 1)
            for (const wound of (this.patient.wounds || []).filter(w => w.practice_type_id == 1)) {
              const treatmentsForEncounter = (wound.treatments || []).filter(
                t => t.encounter_id === this.encounter.id
              )

              for (const t of treatmentsForEncounter) {
                const treatmentToAdd = {
                  treatment_id: t.treatment_id ?? t.id,
                  visit_date: this.encounter.visit_date ?? null,
                  procedure_id: t.procedure_id ?? null,
                  dressing_used_1: t.wound_treatment?.dressing_used_1 ?? null,
                  dressing_used_2: t.wound_treatment?.dressing_used_2 ?? null,
                  dressing_used_other: t.wound_treatment?.dressing_used_other ?? null,
                  primary_area: t.wound_treatment?.wound_treatment_wound_size?.primary_area ?? null,
                  primary_length: t.wound_treatment?.wound_treatment_wound_size?.primary_length ?? null,
                  primary_width: t.wound_treatment?.wound_treatment_wound_size?.primary_width ?? null,
                  primary_depth: t.wound_treatment?.wound_treatment_wound_size?.primary_depth ?? null,
                  wound_stage: t.wound_treatment?.wound_treatment_wound_size?.wound_stage ?? null
                }

                await addTreatmentToReservedData(wound.id, treatmentToAdd)
              }
            }

            this.noteKey += 1
          }
        })
      }
    },
    unsignNote() {
      this.$root.confirm({
        title: 'Unsign Encounter Note?',
        body: 'Are you sure you wish to unsign this encounter note?',
        confirm: 'Unsign Note',
        confirmIcon: this.icons.mdiUndoVariant,
        confirmColor: 'warning',
      }).then(async result => {
        if (result) {
          this.encounter.is_signed = false
          this.encounter.is_synced = false
          this.encounter.signed_by_user_id = null
          this.encounter.signed_time = null
          this.encounter.modified = this.$custom.utcNow()
          this.$store.commit('encounters/updateEncounter', { encounter: this.encounter })

          // Remove treatments linked to this encounter from amnioReqData
          for (const wound of (this.patient.wounds || []).filter(w => w.practice_type_id == 1)) {
            const treatmentsForEncounter = (wound.treatments || []).filter(
              t => t.encounter_id === this.encounter.id
            )

            for (const t of treatmentsForEncounter) {
              await removeTreatmentFromReservedData(wound.id, t.id)
            }
          }

          this.noteKey += 1
        }
      })
    },
    syncNoteOnly(messageSubject = 'Single Note Sync: Post Encounters') {
      if (this.isOnline) {
        this.encounter.sync_log = this.syncLogSetup(messageSubject)
        this.$store.dispatch('encounters/syncingProgress', { pos: 0, cnt: 1 })
        this.$store.dispatch('encounters/syncingModal', true)
        this.$store.dispatch('encounters/syncEncounter', { data: this.encounter, retries: 3 })
          .then(encounterRes => {
            if (typeof encounterRes === 'string') throw new Error(encounterRes)
            this.$store.dispatch('encounters/syncingModal', false)
            const { responseData } = encounterRes

            if (Array.isArray(responseData) && responseData.length > 0 && responseData[0].status === 'Success') {
              this.$store.dispatch('notify', { value: `${messageSubject} synced successfully.`, color: 'success' })
            } else {
              this.encounter.is_synced = false
              let errorMsg = ''

              if (typeof responseData === 'string') {
                errorMsg = responseData
              } else if (Array.isArray(responseData) && responseData.length > 0 && responseData[0].message) {
                errorMsg = responseData[0].message
              } else {
                errorMsg = 'An unexpected error occurred during sync. Please try again.'
              }

              if (errorMsg.includes('1205') || errorMsg.toLowerCase().includes('lock')) {
                errorMsg = 'Server is busy. Please try again in a few moments.'
              }

              this.errorDialog(errorMsg, `${messageSubject} failed to sync!`)
            }
            this.$store.commit('encounters/updateEncounter', { encounter: this.encounter })
          })
          .catch(error => {
            let errorMessage = error.message || error
            if (typeof errorMessage !== 'string') errorMessage = String(errorMessage)

            if (errorMessage.includes('1205') || errorMessage.toLowerCase().includes('lock')) {
              errorMessage = 'Server is busy. Please try again in a few moments.'
            } else if (errorMessage.toLowerCase().includes('network error')) {
              errorMessage += '<br><br>Please check your internet connection and try to sync again. If the problem persists, contact IT Support.'
            }

            this.errorDialog(errorMessage)
          })
      } else {
        // Mark encounter as needed to be synced
        this.encounter.is_synced = false
        this.$store.commit('encounters/updateEncounter', { encounter: this.encounter })

        this.$store.dispatch('notify', { value: 'Addendum successfully added. Connect online to sync.', color: 'warning' })

        // Set sync status to false, user would need to sync the addendum
        this.$store.dispatch('encounters/setSynced', false)
      }
    },
    syncNote() {
      this.patient.sync_log = this.syncLogSetup('Single Note Sync: Post Core Patients')
      this.encounter.sync_log = this.syncLogSetup('Single Note Sync: Post Encounters')
      this.syncSuccessful = false
      this.$store.dispatch('encounters/syncingProgress', { pos: 0, cnt: 3 })
      this.$store.dispatch('encounters/syncingModal', true)
      this.$store.dispatch('patients/syncPatient', {
        data: {
          ...this.patient,
          force_add: true, // Force add the patient, even if a duplicate is detected
        },
        retries: 3

      }).then(corePatientRes => {
        if (typeof corePatientRes === 'string') throw new Error(corePatientRes)
        this.$store.dispatch('encounters/syncingProgress', { pos: 1, cnt: 3 })
        const corePatientResponseData = corePatientRes.responseData
        const patientData = this.$store.getters['patients/getById'](corePatientResponseData.patientID)

        if (Array.isArray(corePatientResponseData) && corePatientResponseData.length > 0 && corePatientResponseData[0]?.status === 'Success') {
          const userId = this.$scribeProvider.id ?? this.$authUser.userId()
          this.encounter.created_by_user_id = userId
          this.encounter.is_synced = true
          this.sanitizeEncounter()
          this.$store.dispatch('encounters/syncEncounter', { data: this.encounter, retries: 3 })
            .then(encounterRes => {
              if (typeof encounterRes === 'string') throw new Error(encounterRes)
              this.$store.dispatch('encounters/syncingProgress', { pos: 2, cnt: 3 })
              const encounterResponseData = encounterRes.responseData
              if (Array.isArray(encounterResponseData) && encounterResponseData.length > 0 && encounterResponseData[0]?.status === 'Success') {
                this.patient.sync_log = this.syncLogSetup('Single Note Sync: Post Full Patients')
                this.$store.dispatch('patients/syncFullPatient', {
                  data: {
                    ...this.patient
                  },
                  retries: 3
                }).then(fullPatientRes => {
                  if (typeof fullPatientRes === 'string') throw new Error(fullPatientRes)
                  const fullPatientResponseData = fullPatientRes.responseData
                  if (Array.isArray(fullPatientResponseData) && fullPatientResponseData.length > 0 && fullPatientResponseData[0]?.status === 'Success') {
                    // Any new attachments were synced successfully, remove updated flag
                    this.patient.all_attachments?.forEach(attachment => {
                      delete attachment.updated
                    })

                    // Remove updated and can_be_deleted flags from wounds, treatments, and amnio_grafts
                    this.patient.wounds?.forEach(wound => {
                      // Remove flags from wound
                      delete wound.updated
                      delete wound.can_be_deleted

                      // Remove treatments marked as deleted
                      wound.treatments = wound.treatments?.filter(treatment => !treatment.deleted) || []

                      // Clean up each remaining treatment
                      wound.treatments.forEach(treatment => {
                        delete treatment.updated
                        delete treatment.can_be_deleted

                        // Remove amnio_grafts marked as deleted
                        const amnioGrafts = treatment.wound_treatment?.amnio_grafts
                        if (Array.isArray(amnioGrafts)) {
                          treatment.wound_treatment.amnio_grafts = amnioGrafts.filter(graft => !graft.deleted)

                          // Remove can_be_deleted flags from remaining grafts
                          treatment.wound_treatment.amnio_grafts.forEach(graft => {
                            delete graft.can_be_deleted
                          })
                        }
                      })
                    })

                    // Mark patient as synced
                    delete this.patient.updated

                    this.$store.commit('patients/updatePatientSync', this.patient)
                    this.$store.dispatch('auth/refresh')
                      .catch(error => {
                        setTimeout(() => {
                          this.$store.dispatch('notify', { value: error , color: 'error' })
                        }, 1000);
                      })
                    this.$store.dispatch('encounters/getEstimatedPayment', this.encounter.id).then(paymentData => {
                      if (paymentData) {
                        this.$store.dispatch('encounters/syncingModal', false);

                        // Update estimated payment and amnio-specific payment
                        this.encounter.estimated_payment = paymentData.estimated_payment;
                        this.encounter.pay_total_amnio = paymentData.pay_total_amnio;

                        if (!this.showPaymentModal) {
                          this.$store.dispatch('notify', {
                            value: 'Encounter note synced successfully.',
                            color: 'success'
                          });
                        }

                        this.$store.commit('encounters/updateEncounter', { encounter: this.encounter });
                        this.syncSuccessful = true;
                        this.noteKey += 1;
                      } else {
                        this.errorDialog(paymentData, 'Encounter failed to sync!');
                      }
                    })
                  } else {
                    let patientErrorsDisplay = 'Full Patient Sync failed'
                    let alternativeMessage = ''
                    console.log(corePatientResponseData)
                    if (corePatientResponseData.message.toLowerCase().includes('1205 lock')) {
                      alternativeMessage = '<strong>Reason:</strong> Server is busy<br>'
                    } else {
                      alternativeMessage = `<strong>Reason:</strong> ${corePatientResponseData.message}<br>}`
                    }

                    patientErrorsDisplay += `
                      <p style="font-size: 11px;">
                      <strong>Name:</strong> ${patientData.first_name} ${patientData.last_name}<br>
                      <strong>DOB:</strong> ${this.$date(patientData.dob).format('MM/DD/YYYY')}<br>
                      ${alternativeMessage}
                      </p>`

                    // fullPatient sync failed...
                    this.errorDialog(patientErrorsDisplay)
                  }
                })
                .catch(error => {
                  let errorMessage = error.message || error // Ensure it's a string
                  if (errorMessage.toLowerCase().includes('network error')) {
                    errorMessage += '<br><br>Please check your internet connection and try to sync again. If the problem persists, contact IT Support.'
                  }
                  this.errorDialog(errorMessage)
                })
              } else {
                let encounterErrorsDisplay = 'Encounter Sync failed'
                let alternativeMessage = ''
                if (encounterRes[0].message.toLowerCase().includes('1205 lock')) {
                  alternativeMessage = '<strong>Reason:</strong> Server is busy<br>'
                } else {
                  alternativeMessage = `<strong>Reason:</strong> ${encounterRes[0].message}<br>`
                }

                encounterErrorsDisplay += `
                <p style="font-size: 11px;">
                <strong>Encounter Id:</strong> ${this.encounter.id}<br>
                <strong>Patient:</strong> ${this.patient.first_name} ${this.patient.last_name}<br>
                <strong>Date Of Service:</strong> ${this.$date(this.encounter.created).format('MM/DD/YYYY HH:mm')}<br>
                ${alternativeMessage}
                </p>`

                // encounter sync failed...
                this.errorDialog(encounterErrorsDisplay)
              }
            })
            .catch(error => {
              let errorMessage = error.message || error // Ensure it's a string
              if (errorMessage.toLowerCase().includes('network error')) {
                errorMessage += '<br><br>Please check your internet connection and try to sync again. If the problem persists, contact IT Support.'
              }
              this.errorDialog(errorMessage)
            })
        } else {
          let patientErrorsDisplay = 'Core Patient Sync failed'
          let alternativeMessage = ''

          if (corePatientResponseData.message.toLowerCase().includes('1205 lock')) {
            alternativeMessage = '<strong>Reason:</strong> Server is busy<br>'
          } else {
            alternativeMessage = `
            <strong>Reason:</strong> ${corePatientResponseData.message}<br>}
             <p style="color: red;">
             <strong>DO NOT ATTEMPT TO RESET THE LOCAL STORAGE AS IT WILL NOT RESOLVE THIS ISSUE AND WILL ONLY DELETE UNSYNCED RECORDS!</strong>
             </p>`
          }

          patientErrorsDisplay += `
          <p style="font-size: 11px;">
          <strong>Name:</strong> ${patientData.first_name} ${patientData.last_name}<br>
          <strong>DOB:</strong> ${this.$date(patientData.dob).format('MM/DD/YYYY')}<br>
          ${alternativeMessage}
          </p>`

          // corePatient sync failed...
          this.errorDialog(patientErrorsDisplay)
        }
      })
      .catch(error => {
        let errorMessage = error.message || error // Ensure it's a string
        if (errorMessage.toLowerCase().includes('network error')) {
          errorMessage += '<br><br>Please check your internet connection and try to sync again. If the problem persists, contact IT Support.'
        }
        this.errorDialog(errorMessage)
      })
    },
    errorDialog(response, defaultMessage) {
      this.$store.dispatch('encounters/syncingModal', false)
      this.encounter.is_synced = false
      this.$store.commit('encounters/updateEncounter', { encounter: this.encounter })
      this.noteKey += 1
      this.$root.confirm({
        titleIconColor: 'error',
        title: 'Sync Error!',
        body: response === false ? defaultMessage : response,
        cancel: false,
        html: true,
      })
    },
    syncNoteQuery() {
      // check for unsynced encounters for this patient
      const hasUnsyncedNote = this.$store.getters['encounters/unsyncedEncounters'](this.encounter.patient_id, this.encounter.practice_type_id, this.encounter.id, 365)

      // if there is a previous encounter that is unsynced then trigger
      if (hasUnsyncedNote && hasUnsyncedNote.length > 0) {
        this.hasUnsyncedEncounters(hasUnsyncedNote)

        return
      }

      this.$root.confirm({
        title: 'Sync Encounter Note?',
        alert: true,
        alertText: 'This operation cannot be undone!',
        alertColor: 'error',
        alertWarning: true,
        body: 'Are you sure you wish to sync this encounter note?',
        confirm: 'Sync Note',
        confirmIcon: this.icons.mdiFileSync,
        confirmColor: 'error',
      }).then(result => {
        if (result) this.syncNote()
      })
    },
    hasUnsyncedEncounters(encounters) {
      // notify & navigate the user to sync that encounter before proceeding
      this.$root.confirm({
        title: 'Notice: unsynced previous encounter',
        subTitle: 'Warning: attempting to sync with an unsynced previous encounter for the same patient will result in errors.',
        body: `Sync ${encounters.length + 1} notes(s) relating to this patient?`,
        confirm: 'Sync Notes',
        confirmColor: 'success',
        large: true,
      }).then(result => {
        if (result) {
          this.patientEncountersConfirmation(encounters)
        }
      })
    },
    patientEncountersConfirmation(encounters) {
      this.$root.confirm({
        title: 'Sync All Encounters for this Patient?',
        subTitle: 'Warning: This operation cannot be undone!',
        body: 'Are you sure you wish to sync all encounters for this patient?',
        confirm: 'Sync Data',
        confirmIcon: this.icons.mdiSync,
        confirmColor: 'error',
      }).then(result => {
        if (result === true) {
          this.syncPatientEncounters(encounters)
        }
      })
    },
    async syncPatientEncounters(unsyncedEncounters) {
      this.patient.sync_log = this.syncLogSetup('Patient Encounters Sync: Post Core Patients')
      unsyncedEncounters.push(this.encounter)
      unsyncedEncounters[0].sync_log = this.syncLogSetup('Patient Encounters Sync: Post Encounters')

      let message = ''
      let response = ''

      // We only need to sync this note and any previous note for this patient
      try {
        // Reset the sync progress values
        this.syncPos = 0
        this.syncCnt = 5

        // Start the sync process
        this.syncingProgress()
        this.$store.dispatch('encounters/syncingModal', true)
        console.log('---------- Starting Individual Patient Sync ----------')

        // Syncing Core Patient
        message = 'Patient Core'
        const patientCoreRes = await this.syncCorePatients(this.patient)
        if (patientCoreRes !== true) {
          response += `${patientCoreRes} <hr><br>`
        }
        this.syncingProgress()
        console.log(`Syncing complete for ${message}`)

        console.log('---------- Starting Individual Patient Encounters Sync ----------')

        // Syncing Encounters
        message = 'Encounters'
        const encountersRes = await this.syncEncounters(unsyncedEncounters)
        if (encountersRes !== true) {
          response += `${encountersRes} <hr><br>`
        }
        this.syncingProgress()
        console.log(`Syncing complete for ${message}`)

        console.log('---------- Starting Full Patient Sync ----------')

        // Syncing Full Patient
        message = 'Full Patient'
        this.patient.sync_log = this.syncLogSetup('Patient Encounters Sync: Post Full Patients')
        const patientFullRes = await this.syncFullPatients(this.patient)

        if (patientFullRes === true) {
          this.syncingProgress()
          console.log(`Syncing complete for ${message}`)
          console.log('Completed posting patient encounters...')
          this.$store.dispatch('auth/refresh')
            .catch(error => {
              setTimeout(() => {
                this.$store.dispatch('notify', { value: error , color: 'error' })
              }, 1000);
            })
          console.log('---------- Sync Completed Successfully ----------')
          this.$store.dispatch('notify', { value: 'Synced Individual Patient Notes.' })
          this.$store.dispatch('encounters/syncingModal', false)
        } else {
          this.$store.dispatch('encounters/setSynced', false)
          response += `${patientFullRes} <hr><br>`
        }

        console.log(response)

        // Throw error if any sync failed
        if (response !== '') throw response
      } catch (Error) {
        response = `Error ocurred with individual patient sync Error : ${Error}`
        this.$store.dispatch('encounters/syncingModal', false)
        this.errorDialog(response)
      }
    },

    getPDF() {
      // download pdf file?
    },
    toggleRedact() {
      this.isRedacted = !this.isRedacted
    },
    addAddendum() {
      this.addendum = null
      this.addingAddendum = true
    },
    cancelAddendum() {
      this.addendum = null
      this.addingAddendum = false
    },

    // TODO: Save addendum should also sync the addendum in real time
    // TODO: Possibly decouple addendum from final note to reduce memory usage
    saveAddendum() {
      this.$root.confirm({
        title: 'Save Addendum?',
        alert: true,
        alertText: 'This operation cannot be undone!',
        alertColor: 'error',
        alertWarning: true,
        html: true,
        body: `Are you sure you wish to save this addendum?<br><br><p>${this.addendum}</p>`,
        confirm: 'Save Addendum',
        confirmIcon: this.icons.mdiContentSaveEdit,
        confirmColor: 'error',
      }).then(result => {
        if (!result) return

        if (this.encounter.practice_type_id === 1) {
          // Regenerate to avoid empty data
          this.generateFinalNoteText()
          this.generateFinalNoteBody()
        }

        // Validate non-empty final note data
        if (
          !this.encounter.is_deferred_care
          && this.encounter.practice_type_id === 1
          && this.encounter.is_signed
          && (!this.patient.is_loaded
            || this.$custom.isEmpty(this.final_note.body)
            || this.$custom.isEmpty(this.encounter.assessment_plan?.assessment_plan))
        ) {
          this.$store.dispatch('notify', {
            value: 'Note data is still loading. Please close and reopen the note before adding the addendum.',
            color: 'error',
          })
          return
        }

        // Push addendum to encounter addendum array
        this.encounter.addendums.push({
          id: uuidv4(),
          place_of_service_id: this.encounter.place_of_service_id,
          patient_id: this.encounter.patient_id,
          encounter_id: this.encounter.id,
          notes: this.addendum,
          created_by_user_id: this.$authUser.userId(),
          created: this.$custom.utcNow(),
        })

        // Reset and sync note with updated addendums/final note
        this.addendum = null
        this.addingAddendum = false

        // Generate a final note depending on the encounter type
        if (!this.encounter.is_deferred_care) {
          if (this.encounter.practice_type_id === 1) {
            this.generateFinalNoteFooter()
            this.encounter.final_note = `${this.final_note.text} ${this.final_note.body} ${this.final_note.footer}`
              .replaceAll('. ', '.&nbsp;')
              .replaceAll('<br>\t', '<br>&nbsp;&nbsp;')
          } else {
            this.generateFinalNote()
            this.encounter.final_note = this.final_note
              .replaceAll('. ', '.&nbsp;')
              .replaceAll('<br>\t', '<br>&nbsp;&nbsp;')
          }
        } else {
          this.generateDeferredCareFinalNote()
        }

        // Temporary variable to prevent offline un-signing of a note
        this.encounter.isAddendumAdded = true

        // Set following values to default value, flagging the encounter note PDF to be redelivered to the facility via
        // 1. manual upload
        // 2. fax
        // 3. PCC
        this.encounter.is_manual_upload = 0
        this.encounter.fax_status_id = 0
        this.encounter.pcc_status_id = 0

        this.encounter.is_revised = 1

        // Values that should be modified when updating an encounter record
        this.encounter.modified = this.$custom.utcNow()

        this.syncNoteOnly('Single Note Sync: Post Encounters Addendum')
      })
    },
    generateDeferredCareFinalNote() {
      let finalNote = `<b>Deferred Care Reason:</b><br>${this.encounter.deferred_care_reason}`
      finalNote += this.generateFinalNoteAddendum()
      this.encounter.final_note = finalNote
        .replaceAll('. ', '.&nbsp;')
        .replaceAll('<br>\t', '<br>&nbsp;&nbsp;')
    },
  },
}
